import React, { useCallback, useEffect, useRef, useState } from "react";
import Image from "next/image";
import { useRouter } from "next/router";
import { useTranslations } from "next-intl";
import { toast } from "react-toastify";
import Button from "@elements/button";
import LeftArrow from "@svg/LeftArrow";
import Loading from "assets/lottie/loading";
import ramadanRiddlesApi from "@api/ramadanRiddlesApi";
import type { IRamadanRiddleUserReward } from "@types";
import { useAppContext } from "@context/app";
import { segmentEvent } from "@utils/segment";
import RamadanRiddlesRewardView from "./RamadanRiddlesRewardView";
import RamadanRewardCard from "./RamadanRewardCard";
import {
    useRiddlesActions,
    useRiddlesNavigation,
    useRiddlesRewards,
} from "../RamadanRiddlesContext";

const RamadanRiddlesRewards: React.FC = () => {
    const t = useTranslations();
    const { locale } = useRouter();
    const { setView } = useRiddlesNavigation();
    const { user, country, area, eventProperties } = useAppContext();
    const {
        data: rewardsData,
        isLoading: isRewardsLoading,
        error: rewardsError,
    } = useRiddlesRewards();
    const { setRewards, setRewardsError, setRewardsLoading } = useRiddlesActions();
    const isFetchingRef = useRef(false);
    const isMountedRef = useRef(true);
    const hasTrackedViewRef = useRef(false);
    const [selectedReward, setSelectedReward] = useState<IRamadanRiddleUserReward | null>(null);
    const rewards = rewardsData?.rewards ?? [];
    const language = locale?.includes("en") ? "English" : "Arabic";

    const handleOpenReward = (reward: IRamadanRiddleUserReward) => {
        setSelectedReward(reward);
    };

    const handleCloseReward = () => {
        setSelectedReward(null);
    };

    const fetchRewards = useCallback(async () => {
        if (isFetchingRef.current) return;
        isFetchingRef.current = true;
        const defaultErrorMessage = t("client_general_error");
        setRewardsError(null);
        setRewardsLoading(true);

        try {
            const response = await ramadanRiddlesApi.getRewards().fetch();
            if (!isMountedRef.current) return;

            if (response?.meta?.code === 200) {
                setRewards(response.data);
            } else {
                const errorMessage = response?.meta?.msg || defaultErrorMessage;
                setRewardsError(errorMessage);
                toast.error(errorMessage);
            }
        } catch (error) {
            console.error("Failed to load Ramadan riddles rewards:", error);
            if (isMountedRef.current) {
                setRewardsError(defaultErrorMessage);
                toast.error(defaultErrorMessage);
            }
        } finally {
            if (isMountedRef.current) {
                setRewardsLoading(false);
            }
            isFetchingRef.current = false;
        }
    }, [setRewards, setRewardsError, setRewardsLoading, t]);

    useEffect(() => {
        isMountedRef.current = true;
        fetchRewards();

        return () => {
            isMountedRef.current = false;
        };
    }, [fetchRewards]);

    useEffect(() => {
        if (hasTrackedViewRef.current) return;
        hasTrackedViewRef.current = true;

        segmentEvent("Ramadan Your Rewards Page Opened", {
            ...eventProperties,
            area_id: area?.id,
            area_slug: area?.slug,
            country_id: country?.id,
            country_slug: country?.slug,
            language,
            user_id: user?.data?.id || -1,
        });
    }, [
        area?.id,
        area?.slug,
        country?.id,
        country?.slug,
        eventProperties,
        language,
        user?.data?.id,
    ]);

    if (selectedReward) {
        return <RamadanRiddlesRewardView reward={selectedReward} onBack={handleCloseReward} />;
    }

    return (
        <div className="flex h-full flex-col bg-[#FDDBA6] text-[#38190D]">
            <div className="relative">
                <Image
                    src="/svgs/ramadan-riddle-misbah-hilal-header.svg"
                    alt=""
                    aria-hidden="true"
                    width={333}
                    height={229}
                    sizes="100vw"
                    className="h-auto mx-auto w-[85%]"
                />
                <button
                    type="button"
                    onClick={() => setView("progress")}
                    className="absolute left-5 top-4 text-[#38190D]"
                    aria-label={t("Back")}
                >
                    <LeftArrow />
                </button>
            </div>

            <div className="flex-1 flex flex-col px-6 pb-8">
                {isRewardsLoading ? (
                    <div className="flex flex-1 items-center justify-center py-10">
                        <Loading isContained />
                    </div>
                ) : rewardsError ? (
                    <div className="flex flex-1 flex-col items-center justify-center gap-4 text-center">
                        <p className="text-sm text-[#956C4A]">
                            {rewardsError || t("client_general_error")}
                        </p>
                        <Button
                            theme="light"
                            className="h-10 w-full rounded-full !bg-[#F27D23] !text-white !border-[#F27D23] font-extrabold"
                            onClick={fetchRewards}
                        >
                            {t("Try again")}
                        </Button>
                    </div>
                ) : rewards.length === 0 ? (
                    <div className="pt-4 my-auto flex flex-col items-center gap-4 text-center">
                        <div className="max-w-64">
                            <h2 className="text-lg font-extrabold uppercase text-[#38190D]">
                                {t("No rewards yet")}
                            </h2>
                            <p className="my-4 text-sm text-[#956C4A]">
                                {t("Your Ramadan rewards will appear here once you start playing")}
                            </p>
                            <p className="my-4 whitespace-pre-line text-sm text-[#956C4A]">
                                {t("Answer today's question to unlock your first surprise")}
                            </p>
                        </div>
                        <Button
                            theme="light"
                            className="h-10 w-full rounded-full !bg-[#F27D23] !text-white !border-[#F27D23] font-extrabold"
                            onClick={() => setView("progress")}
                        >
                            {t("Play Today's Question")}
                        </Button>
                    </div>
                ) : (
                    <>
                        <h2 className="mt-2 text-lg font-extrabold uppercase text-[#38190D]">
                            {t("Your Rewards")}
                        </h2>
                        <div className="mt-4 flex flex-col gap-3">
                            {rewards.map((reward) => (
                                <RamadanRewardCard
                                    key={reward.id}
                                    reward={reward}
                                    onViewReward={handleOpenReward}
                                />
                            ))}
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default RamadanRiddlesRewards;
